import React, { useState, useEffect, useCallback, useRef } from 'react'
import Select from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { Badge } from './Badge'
import { Banner } from './Banner'
import { Button } from './Button'
import { SelectableCard } from './SelectableCard'
import { Modal } from './Modal'
import { Panel } from './Panel'
import { PublishingAuditModal } from './PublishingAuditModal'
import { Spinner } from './Spinner'
import { Tabs } from './Tabs'
import { api } from '../services/api'
import selectStyles from '../styles/react-select.module.scss'
import styles from './PublishingWorkflow.module.scss'
import { PubContent, Competition } from '../types/match'

interface PublishingWorkflowProps {
    matchId: string
    pubContent?: PubContent | null
    /** Competition data for determining default shoulder VOD checkbox state */
    competition?: Competition | null
    onUpdate: () => void
    /** Makes the panel collapsible */
    collapsible?: boolean
    /** Start collapsed (only used if collapsible is true) */
    defaultCollapsed?: boolean
    /** Venue timezone for displaying court time in audit log */
    venueTimezone?: string | null
}

export const PublishingWorkflow: React.FC<PublishingWorkflowProps> = ({
    matchId,
    pubContent,
    competition,
    onUpdate,
    collapsible = false,
    defaultCollapsed = false,
    venueTimezone,
}) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)
    // Internal state management
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [selectedChannels, setSelectedChannels] = useState<string[]>([])
    const [selectedCountries, setSelectedCountries] = useState<string[]>([])
    const [selectedTags, setSelectedTags] = useState<string[]>([])
    const [selectedFeedOverride, setSelectedFeedOverride] = useState<string | null>(null)
    const [channelOptions, setChannelOptions] = useState<Array<{ value: string, label: string }>>([])
    const [countryOptions, setCountryOptions] = useState<Array<{ value: string, label: string }>>([])
    const [tagOptions, setTagOptions] = useState<Array<{ value: string, label: string }>>([])
    const [feedSourceOptions, setFeedSourceOptions] = useState<Array<{ value: string, label: string }>>([])
    const [scheduledDateTime, setScheduledDateTime] = useState('')
    const [publishMode, setPublishMode] = useState<'now' | 'scheduled'>('now')
    const [showPreview, setShowPreview] = useState(false)
    const [previewData, setPreviewData] = useState<any>(null)
    const [previewTab, setPreviewTab] = useState<'asset' | 'sns' | 'withdraw'>('asset')
    const [previewLoading, setPreviewLoading] = useState(false)
    const [previewError, setPreviewError] = useState<string | null>(null)
    const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false)
    const hasFetchedOptions = useRef(false)
    const [title, setTitle] = useState('')
    const [description, setDescription] = useState('')
    const [synopsis, setSynopsis] = useState('')
    const [showAuditModal, setShowAuditModal] = useState(false)
    const [lastPublishError, setLastPublishError] = useState<{
        message: string
        timestamp: string
        details?: any
    } | null>(null)
    const [showWithdrawModal, setShowWithdrawModal] = useState(false)
    const [withdrawReason, setWithdrawReason] = useState('')
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [includeShoulderVod, setIncludeShoulderVod] = useState(false)
    const [hasInitializedShoulderVod, setHasInitializedShoulderVod] = useState(false)

    // Convert null to undefined for API calls (null = virtual, undefined = create new)
    const contentId = pubContent?.tc_pub_content_id ?? undefined

    // Determine default checkbox state based on competition type
    // ATP/WTA Singles = checked by default, all others = unchecked
    const isAtpOrWta = competition?.category === 'ATP' || competition?.category === 'WTA'
    const isSingles = (competition?.tc_type || competition?.ext_type)?.toLowerCase() === 'singles'
    const defaultShoulderVodChecked = isAtpOrWta && isSingles

    // Check for recent publishing errors from audit log
    const checkForPublishingErrors = useCallback(async () => {
        if (!contentId) return // Can't fetch history for virtual pub_content
        try {
            const response = await api.get(`/matches/publishing/${contentId}/history`)
            const history = response.history || []

            if (history.length === 0) {
                setLastPublishError(null)
                return
            }

            // Find the most recent PUBLISH_ERROR
            const lastError = history.find((entry: any) => entry.action === 'PUBLISH_ERROR')

            // Find the most recent successful publish action
            const lastSuccess = history.find((entry: any) =>
                ['PUBLISH', 'REPUBLISH', 'AUTO_REPUBLISH'].includes(entry.action)
            )

            // If there's an error that's more recent than the last successful publish
            if (lastError) {
                const errorTime = new Date(lastError.created_at).getTime()
                const successTime = lastSuccess ? new Date(lastSuccess.created_at).getTime() : 0

                if (errorTime > successTime) {
                    const errorDetails = lastError.details || {}
                    setLastPublishError({
                        message: errorDetails.error || 'Publishing failed',
                        timestamp: lastError.created_at,
                        details: errorDetails,
                    })
                    return
                }
            }

            setLastPublishError(null)
        } catch (err) {
            console.error('Failed to check for publishing errors:', err)
            // Don't set an error state here - this is just a background check
        }
    }, [contentId])

    // Check for publishing errors on mount and when publishing state changes
    useEffect(() => {
        if (pubContent?.tc_pub_content_id) {
            checkForPublishingErrors()
        }
    }, [pubContent?.tc_pub_content_id, pubContent?.status, checkForPublishingErrors])

    const handleUpdateChannels = useCallback(async (channels: string[]) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.patch(`/matches/${matchId}/channels`, { channels, ...(contentId && { content_id: contentId }) })
            setSelectedChannels(channels)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to update channels')
        } finally {
            setIsProcessing(false)
        }
    }, [matchId, onUpdate, contentId])

    const handleUpdateCountries = useCallback(async (countries: string[]) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.patch(`/matches/${matchId}/countries`, { countries, ...(contentId && { content_id: contentId }) })
            setSelectedCountries(countries)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to update countries')
        } finally {
            setIsProcessing(false)
        }
    }, [matchId, onUpdate, contentId])

    const handleUpdateTags = useCallback(async (tags: string[]) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.put(`/matches/${matchId}/tags`, { tags })
            setSelectedTags(tags)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to update tags')
        } finally {
            setIsProcessing(false)
        }
    }, [matchId, onUpdate])

    const handleCreateTag = useCallback(async (tagName: string) => {
        try {
            const response = await api.post('/publishing/tags', { tag_name: tagName })
            // Refresh tag options
            const tags = await api.get('/publishing/tags').then(r => r.tags || [])
            setTagOptions(tags)
            return response.tag
        } catch (err: any) {
            setError(err.message || 'Failed to create tag')
            throw err
        }
    }, [])

    const handleUpdateFeedOverride = useCallback(async (feedId: string | null) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.patch(`/matches/${matchId}/feed-override`, { feed_id: feedId })
            setSelectedFeedOverride(feedId)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to update feed override')
        } finally {
            setIsProcessing(false)
        }
    }, [matchId, onUpdate])


    // Fetch channels and countries options on mount (only once)
    useEffect(() => {
        if (hasFetchedOptions.current) return
        hasFetchedOptions.current = true

        const fetchOptions = async () => {
            try {
                const [channels, countries, feedSources, tags] = await Promise.all([
                    api.get('/publishing/channels').then(r => r.channels || []),
                    api.get('/publishing/countries').then(r => r.countries || []),
                    api.get('/publishing/feed-sources').then(r => r.feed_sources || []),
                    api.get('/publishing/tags').then(r => r.tags || [])
                ])
                setChannelOptions(channels)
                setCountryOptions(countries)
                setFeedSourceOptions(feedSources.map((fs: { value: string; label: string }) => ({ value: fs.value, label: fs.label })))
                setTagOptions(tags)
            } catch (err) {
                console.error('Failed to fetch publishing options:', err)
                setError('Failed to load channels/countries/feed sources options')
            }
        }
        fetchOptions()
    }, [])

    // Initialize from pubContent state
    useEffect(() => {
        if (!hasInitializedDefaults) {
            if (pubContent) {
                // Use pubContent state directly - backend handles defaults for virtual pub_content
                // No fallbacks: if these are null, the API contract is broken and we want to know
                setSelectedChannels(pubContent.channels)
                setSelectedCountries(pubContent.countries)

                // Fetch and set tags
                const fetchTags = async () => {
                    try {
                        const tags = await api.get(`/matches/${matchId}/tags`).then(r => r.tags || [])
                        setSelectedTags(tags || [])
                    } catch (err) {
                        console.error('Failed to fetch match tags:', err)
                    }
                }
                fetchTags()

                // Set feed override if exists
                if (pubContent.feed_override) {
                    setSelectedFeedOverride(pubContent.feed_override.feed_id)
                } else {
                    setSelectedFeedOverride(null)
                }

                // Set metadata fields
                setTitle(pubContent.title || '')
                setDescription(pubContent.description || '')
                setSynopsis(pubContent.synopsis || '')

                if (pubContent.scheduled_publish_at) {
                    // Convert UTC ISO to local datetime-local format
                    const dt = new Date(pubContent.scheduled_publish_at)
                    const localDateTime = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000)
                        .toISOString()
                        .slice(0, 16)
                    setScheduledDateTime(localDateTime)
                }
            } else {
                // No pubContent record yet - show defaults in UI
                setSelectedChannels(['amazon'])
                setSelectedCountries(['USA'])
            }

            setHasInitializedDefaults(true)
        }
    }, [pubContent, hasInitializedDefaults, matchId])

    // Initialize shoulder VOD checkbox from persisted value or default based on competition type
    // Use persisted value if a pub_content record exists (not virtual)
    // Otherwise use the computed default based on competition type
    useEffect(() => {
        if (!hasInitializedShoulderVod) {
            // A pub_content record exists if tc_pub_content_id is set (not a virtual record)
            const hasPubContentRecord = pubContent?.tc_pub_content_id != null
            if (hasPubContentRecord && pubContent?.publish_virtual_vod_assets !== undefined) {
                // Use persisted value from server (user has saved/published before)
                setIncludeShoulderVod(pubContent.publish_virtual_vod_assets)
            } else {
                // First time - use default based on competition type
                setIncludeShoulderVod(defaultShoulderVodChecked)
            }
            setHasInitializedShoulderVod(true)
        }
    }, [pubContent, hasInitializedShoulderVod, defaultShoulderVodChecked])

    const status = pubContent?.status || 'DRAFT'
    const isDirty = pubContent?.is_dirty || false
    const unpublishedChanges = pubContent ? (pubContent.current_version - (pubContent.last_published_ver || 0)) : 0
    const autoRepublish = pubContent?.auto_republish || false

    const handleSaveDraft = async () => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.post(`/matches/${matchId}/workflow/save-draft`, {
                title: title || undefined,
                description: description || undefined,
                synopsis: synopsis || undefined,
                content_id: contentId,
                publish_virtual_vod_assets: includeShoulderVod,
            })
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to save draft')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleSchedule = async () => {
        if (!scheduledDateTime) {
            setError('Please select a scheduled time')
            return
        }
        setIsProcessing(true)
        setError(null)
        try {
            // Convert local datetime to UTC ISO
            const dt = new Date(scheduledDateTime)
            const utcISO = dt.toISOString()
            await api.post(`/matches/${matchId}/workflow/schedule`, {
                scheduled_publish_at: utcISO,
                ...(contentId && { content_id: contentId }),
                publish_virtual_vod_assets: includeShoulderVod,
            })
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to schedule')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleUnschedule = async () => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.post(`/matches/${matchId}/workflow/unschedule`, contentId ? { content_id: contentId } : {})
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to unschedule')
        } finally {
            setIsProcessing(false)
        }
    }

    const handlePublishNow = async () => {
        if (selectedChannels.length === 0) {
            setError('Please select at least one channel')
            return
        }
        if (selectedCountries.length === 0) {
            setError('Please select at least one country')
            return
        }
        setIsProcessing(true)
        setError(null)
        setLastPublishError(null) // Clear any previous publish error
        try {
            await api.post(`/matches/${matchId}/workflow/publish-now`, {
                ...(contentId && { content_id: contentId }),
                publish_virtual_vod_assets: includeShoulderVod,
            })
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to publish')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleRepublish = async () => {
        setIsProcessing(true)
        setError(null)
        setLastPublishError(null) // Clear any previous publish error
        try {
            await api.post(`/matches/${matchId}/workflow/republish`, contentId ? { content_id: contentId } : {})
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to republish')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleCancel = async () => {
        if (!contentId) return
        setIsProcessing(true)
        setError(null)
        try {
            await api.post(`/matches/publishing/${contentId}/cancel`)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to cancel match')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleToggleAutoRepublish = async (enabled: boolean) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.patch(`/matches/${matchId}/workflow/auto-republish`, { enabled, ...(contentId && { content_id: contentId }) })
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to toggle auto-republish')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleToggleIncludeShoulderVod = async (enabled: boolean) => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.patch(`/matches/${matchId}/workflow/include-shoulder-vod`, { enabled, ...(contentId && { content_id: contentId }) })
            setIncludeShoulderVod(enabled)
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to toggle include shoulder VOD')
        } finally {
            setIsProcessing(false)
        }
    }

    const handleWithdraw = async () => {
        setIsProcessing(true)
        setError(null)
        try {
            await api.post(`/matches/${matchId}/workflow/withdraw`, { reason: withdrawReason || undefined })
            setShowWithdrawModal(false)
            setWithdrawReason('')
            onUpdate()
        } catch (err: any) {
            setError(err.message || 'Failed to withdraw match')
        } finally {
            setIsProcessing(false)
        }
    }

    const handlePreview = async () => {
        // Open modal immediately with loading state
        setShowPreview(true)
        setPreviewLoading(true)
        setPreviewError(null)
        setPreviewData(null)

        try {
            const data = await api.get(`/matches/${matchId}/preview`, {
                lang: 'en_US',
                include_shoulder_vod: includeShoulderVod
            })
            setPreviewData(data)
        } catch (err: any) {
            setPreviewError(err.message || 'Failed to load preview')
        } finally {
            setPreviewLoading(false)
        }
    }

    const handleCopyPreview = () => {
        if (previewData) {
            const dataMap = {
                asset: previewData.match_asset,
                sns: previewData.sns_message,
                withdraw: previewData.withdraw_message,
            }
            const dataToCopy = dataMap[previewTab]
            navigator.clipboard.writeText(JSON.stringify(dataToCopy, null, 2))
        }
    }

    return (
        <div className={styles.publishingWorkflow}>
            {/* Header with Status Badge */}
            <Panel.Header
                collapsible={collapsible}
                collapsed={isCollapsed}
                onCollapsedChange={setIsCollapsed}
            >
                <h2>Publishing</h2>
                <Badge variant={
                    status === 'PUBLISHED' ? 'success' :
                        status === 'PUBLISHING' ? 'warning' :
                            status === 'SCHEDULED' ? 'warning' :
                                status === 'WITHDRAWN' ? 'error' :
                                    status === 'CANCELLED' ? 'error' :
                                        'neutral'
                }>
                    {status}
                    {status === 'PUBLISHING' && (
                        <span style={{ marginLeft: '0.25rem' }}>
                            (in progress...)
                        </span>
                    )}
                    {status !== 'PUBLISHING' && status !== 'CANCELLED' && isDirty && unpublishedChanges > 0 && (
                        <span style={{ marginLeft: '0.25rem' }}>
                            ({unpublishedChanges} unpublished)
                        </span>
                    )}
                </Badge>
                {/* Segment indicator */}
                {pubContent && pubContent.segment_number > 1 && (
                    <Badge variant="neutral" className={styles.segmentBadge}>
                        Segment {pubContent.segment_number}
                    </Badge>
                )}
                <Button
                    onClick={(e) => {
                        e.stopPropagation() // Don't trigger collapse
                        setShowAuditModal(true)
                    }}
                    variant="secondary"
                    size="small"
                    className={styles.auditLogButton}
                    disabled={!contentId}
                    title={!contentId ? "Save changes first to view audit log" : undefined}
                >
                    Audit Log
                </Button>
            </Panel.Header>

            {/* Body content - hidden when collapsed */}
            {!isCollapsed && (
                <>
                    {/* Content ID for staff reference */}
                    {pubContent?.tc_pub_content_id && (
                        <p><code className="id-code">{pubContent.tc_pub_content_id}</code></p>
                    )}

                    {error && (
                        <div className={styles.error}>
                            {error}
                        </div>
                    )}

                    {/* Last publishing error banner */}
                    {lastPublishError && (
                        <Banner
                            variant="error"
                            message={`Last publish failed: ${lastPublishError.message}`}
                            onDismiss={() => setLastPublishError(null)}
                        />
                    )}

                    {/* Publishing in progress message */}
                    {status === 'PUBLISHING' && (
                        <Banner
                            variant="warning"
                            message="Publishing in progress..."
                        />
                    )}

                    {/* Cancelled segment message */}
                    {status === 'CANCELLED' && (
                        <Banner
                            variant="error"
                            message="This segment was cancelled."
                        />
                    )}

                    {/* Segment reason info banner */}
                    {pubContent && pubContent.segment_number > 1 && status !== 'CANCELLED' && (
                        <Banner
                            variant="info"
                            message={pubContent.segment_reason === 'SUSPENSION'
                                ? <>This is segment {pubContent.segment_number} from a suspended match. The <strong>resume time</strong> will be used as the start time.</>
                                : <>This is a replacement segment. The <strong>start time</strong> field will be used.</>
                            }
                        />
                    )}

                    {/* Publishing Info */}
                    {(pubContent?.published_at || pubContent?.scheduled_publish_at) && (
                        <>
                            {pubContent?.published_at && (
                                <p className={styles.publishedInfo}>
                                    Published: {new Date(pubContent.published_at).toLocaleString()}
                                </p>
                            )}
                            {pubContent?.scheduled_publish_at && (
                                <p className={styles.scheduledInfo}>
                                    Scheduled: {new Date(pubContent.scheduled_publish_at).toLocaleString()}
                                </p>
                            )}
                        </>
                    )}

                    {/* Content Metadata */}
                    <div className={styles.fieldGrid}>
                        <div className="fieldGroup">
                            <label>Title</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder={pubContent?.auto_title || ""}
                                disabled={isProcessing || status === 'PUBLISHING'}
                                className="input"
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={pubContent?.auto_description || ""}
                                disabled={isProcessing || status === 'PUBLISHING'}
                                className="input"
                                rows={2}
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Synopsis</label>
                            <textarea
                                value={synopsis}
                                onChange={(e) => setSynopsis(e.target.value)}
                                placeholder={pubContent?.auto_synopsis || ""}
                                disabled={isProcessing || status === 'PUBLISHING'}
                                className="input"
                                rows={3}
                                style={{ resize: 'vertical' }}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Channels</label>
                            <Select
                                isMulti
                                options={channelOptions}
                                value={selectedChannels.map(ch => channelOptions.find(opt => opt.value === ch)).filter((opt): opt is { value: string, label: string } => opt !== undefined)}
                                onChange={(selectedOptions) => {
                                    const channels = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                    handleUpdateChannels(channels)
                                }}
                                isDisabled={isProcessing || channelOptions.length === 0 || status === 'PUBLISHING'}
                                className={selectStyles.reactSelect}
                                classNamePrefix="react-select"
                                placeholder={channelOptions.length === 0 ? "Loading channels..." : "Select channels..."}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Countries</label>
                            <Select
                                isMulti
                                options={countryOptions}
                                value={selectedCountries.map(co => countryOptions.find(opt => opt.value === co)).filter((opt): opt is { value: string, label: string } => opt !== undefined)}
                                onChange={(selectedOptions) => {
                                    const countries = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                    handleUpdateCountries(countries)
                                }}
                                isDisabled={isProcessing || countryOptions.length === 0 || status === 'PUBLISHING'}
                                className={selectStyles.reactSelect}
                                classNamePrefix="react-select"
                                placeholder={countryOptions.length === 0 ? "Loading countries..." : "Select countries..."}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Tags/Keywords (optional)</label>
                            <CreatableSelect
                                isMulti
                                options={tagOptions}
                                value={selectedTags.map(tag => ({ value: tag, label: tag }))}
                                onChange={(selectedOptions) => {
                                    const tags = selectedOptions ? selectedOptions.map(opt => opt.value) : []
                                    handleUpdateTags(tags)
                                }}
                                onCreateOption={async (inputValue) => {
                                    try {
                                        await handleCreateTag(inputValue)
                                        // Add the new tag to selected tags
                                        handleUpdateTags([...selectedTags, inputValue])
                                    } catch (err) {
                                        // Error already handled in handleCreateTag
                                    }
                                }}
                                isDisabled={isProcessing || status === 'PUBLISHING'}
                                className={selectStyles.reactSelect}
                                classNamePrefix="react-select"
                                placeholder="Type to create or select tags..."
                                formatCreateLabel={(inputValue) => `Create tag "${inputValue}"`}
                            />
                        </div>

                        <div className="fieldGroup">
                            <label>Feed Override (optional)</label>
                            <Select
                                options={[
                                    { 
                                        value: '', 
                                        label: pubContent?.venue_feed 
                                            ? `Use court default (${pubContent.venue_feed.feed_name})`
                                            : 'Use court default (none mapped)'
                                    },
                                    ...feedSourceOptions
                                ]}
                                value={selectedFeedOverride
                                    ? feedSourceOptions.find(opt => opt.value === selectedFeedOverride)
                                    : { 
                                        value: '', 
                                        label: pubContent?.venue_feed 
                                            ? `Use court default (${pubContent.venue_feed.feed_name})`
                                            : 'Use court default (none mapped)'
                                    }
                                }
                                onChange={(selectedOption) => {
                                    const feedId = selectedOption && selectedOption.value && selectedOption.value !== '' ? selectedOption.value : null
                                    handleUpdateFeedOverride(feedId)
                                }}
                                isDisabled={isProcessing || feedSourceOptions.length === 0 || status === 'PUBLISHING'}
                                className={selectStyles.reactSelect}
                                classNamePrefix="react-select"
                                placeholder={feedSourceOptions.length === 0 ? "Loading feed sources..." : "Select feed source..."}
                                isClearable={false}
                            />
                        </div>
                    </div>

                    {/* Publishing Actions Section */}
                    {status === 'DRAFT' && (
                        <Panel.Footer>
                            <h3 className={styles.publishingModeTitle}>Publishing</h3>

                            <div className={styles.publishingModeOptions}>
                                <SelectableCard
                                    selected={publishMode === 'now'}
                                    onClick={() => setPublishMode('now')}
                                    disabled={isProcessing}
                                    className={styles.radioOption}
                                >
                                    <input
                                        type="radio"
                                        name="publishMode"
                                        value="now"
                                        checked={publishMode === 'now'}
                                        onChange={() => setPublishMode('now')}
                                        disabled={isProcessing}
                                    />
                                    <div className={styles.radioContent}>
                                        <span className={styles.radioLabel}>Publish immediately</span>
                                        <span className={styles.radioDescription}>Make this match available right away</span>
                                    </div>
                                </SelectableCard>

                                <SelectableCard
                                    selected={publishMode === 'scheduled'}
                                    onClick={() => setPublishMode('scheduled')}
                                    disabled={isProcessing}
                                    className={styles.radioOption}
                                >
                                    <input
                                        type="radio"
                                        name="publishMode"
                                        value="scheduled"
                                        checked={publishMode === 'scheduled'}
                                        onChange={() => setPublishMode('scheduled')}
                                        disabled={isProcessing}
                                    />
                                    <div className={styles.radioContent}>
                                        <span className={styles.radioLabel}>Schedule for later</span>
                                        <span className={styles.radioDescription}>Set a specific publish time</span>
                                        {publishMode === 'scheduled' && (
                                            <div className={styles.scheduleTimeInput}>
                                                <label>Publish Date & Time</label>
                                                <input
                                                    type="datetime-local"
                                                    value={scheduledDateTime}
                                                    onChange={(e) => setScheduledDateTime(e.target.value)}
                                                    disabled={isProcessing}
                                                    className="input"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </SelectableCard>
                            </div>

                            {/* Include Shoulder VOD checkbox */}
                            <div className={styles.shoulderVodOption}>
                                <SelectableCard
                                    selected={includeShoulderVod}
                                    onClick={() => handleToggleIncludeShoulderVod(!includeShoulderVod)}
                                    disabled={isProcessing}
                                    className={styles.checkboxOption}
                                >
                                    <input
                                        type="checkbox"
                                        checked={includeShoulderVod}
                                        onChange={(e) => handleToggleIncludeShoulderVod(e.target.checked)}
                                        disabled={isProcessing}
                                    />
                                    <div className={styles.checkboxContent}>
                                        <span className={styles.checkboxLabel}>Include Shoulder VOD</span>
                                    </div>
                                </SelectableCard>
                            </div>

                            <div className={styles.publishingActions}>
                                {publishMode === 'now' ? (
                                    <Button
                                        onClick={handlePublishNow}
                                        disabled={isProcessing || selectedChannels.length === 0 || selectedCountries.length === 0}
                                    >
                                        Publish Now
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleSchedule}
                                        disabled={isProcessing || !scheduledDateTime}
                                    >
                                        Schedule Publish
                                    </Button>
                                )}
                                <Button onClick={handleSaveDraft} disabled={isProcessing} variant="secondary">
                                    Save Draft
                                </Button>
                                <Button onClick={handlePreview} disabled={isProcessing} variant="secondary">
                                    Preview JSON
                                </Button>
                            </div>
                        </Panel.Footer>
                    )}

                    {/* Other status buttons */}
                    {status !== 'DRAFT' && (
                        <div className={styles.publishingActions} style={{ marginTop: '1.5rem' }}>
                            {status === 'SCHEDULED' && (
                                <>
                                    <Button onClick={handleUnschedule} disabled={isProcessing}>
                                        Unschedule
                                    </Button>
                                    <Button onClick={handlePublishNow} disabled={isProcessing || selectedChannels.length === 0 || selectedCountries.length === 0}>
                                        Publish Now (bypass time)
                                    </Button>
                                </>
                            )}
                            {status === 'PUBLISHED' && (
                                <>
                                    <Button onClick={handleSaveDraft} disabled={isProcessing}>
                                        Save Draft
                                    </Button>
                                    <Button
                                        onClick={handleRepublish}
                                        disabled={isProcessing || !isDirty}
                                    >
                                        Republish
                                    </Button>
                                    <Button
                                        onClick={() => setShowCancelModal(true)}
                                        disabled={isProcessing}
                                        variant="danger"
                                    >
                                        Cancel
                                    </Button>
                                    {/*
                            Hide Withdraw button - not implemented on the Amagi/Amazon side.
                            <Button
                                onClick={() => setShowWithdrawModal(true)}
                                disabled={isProcessing}
                                variant="danger"
                            >
                                Withdraw
                            </Button>*/}
                                </>
                            )}
                            {status === 'WITHDRAWN' && (
                                <>
                                    <Button
                                        onClick={handlePublishNow}
                                        disabled={isProcessing || selectedChannels.length === 0 || selectedCountries.length === 0}
                                    >
                                        Republish
                                    </Button>
                                </>
                            )}
                            <Button onClick={handlePreview} disabled={isProcessing} variant="secondary">
                                Preview JSON
                            </Button>
                        </div>
                    )}

                    {/* Auto-republish Toggle (for PUBLISHED status) */}
                    {status === 'PUBLISHED' && (
                        <div className="fieldGroup" style={{ marginTop: '1rem', display: 'flex', alignItems: 'center' }}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={autoRepublish}
                                    onChange={(e) => handleToggleAutoRepublish(e.target.checked)}
                                    disabled={isProcessing}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                Auto-republish
                            </label>
                        </div>
                    )}
                </>
            )}

            {/* Preview Modal */}
            <Modal
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                title="Preview JSON"
                size="xlarge"
                headerActions={
                    <>
                        {!previewLoading && !previewError && previewData && (
                            <Button onClick={handleCopyPreview} variant="secondary" size="small" style={{ marginRight: '0.5rem' }}>
                                Copy
                            </Button>
                        )}
                        <Button onClick={() => setShowPreview(false)} variant="secondary" size="small">
                            Close
                        </Button>
                    </>
                }
            >
                {previewLoading && (
                    <div style={{ padding: '3rem' }}>
                        <Spinner message="Loading preview..." />
                    </div>
                )}

                {previewError && (
                    <Banner
                        variant="error"
                        message={previewError}
                    />
                )}

                {!previewLoading && !previewError && previewData && (
                    <>
                        <Tabs
                            tabs={[
                                {
                                    id: 'asset',
                                    label: `Match Asset (S3)${previewData.validation_errors?.match_asset ? ' ⚠️' : ''}`
                                },
                                {
                                    id: 'sns',
                                    label: `Publish (SNS)${previewData.validation_errors?.sns_message ? ' ⚠️' : ''}`
                                },
                                {
                                    id: 'withdraw',
                                    label: `Withdraw (SNS)${previewData.validation_errors?.withdraw_message ? ' ⚠️' : ''}`
                                }
                            ]}
                            activeTab={previewTab}
                            onTabChange={(tabId) => setPreviewTab(tabId as 'asset' | 'sns' | 'withdraw')}
                        >
                            {/* Show specific error for current tab */}
                            {previewTab === 'asset' && previewData.validation_errors?.match_asset && (
                                <Banner variant="warning" message={previewData.validation_errors.match_asset} />
                            )}
                            {previewTab === 'sns' && previewData.validation_errors?.sns_message && (
                                <Banner variant="warning" message={previewData.validation_errors.sns_message} />
                            )}
                            {previewTab === 'withdraw' && previewData.validation_errors?.withdraw_message && (
                                <Banner variant="warning" message={previewData.validation_errors.withdraw_message} />
                            )}
                            <pre className={styles.previewCode}>
                                {previewData && previewTab === 'asset' && JSON.stringify(previewData.match_asset, null, 2)}
                                {previewData && previewTab === 'sns' && JSON.stringify(previewData.sns_message, null, 2)}
                                {previewData && previewTab === 'withdraw' && JSON.stringify(previewData.withdraw_message, null, 2)}
                            </pre>
                        </Tabs>
                    </>
                )}
            </Modal>

            {/* Publishing Audit Modal - only available for persisted pub_content */}
            {contentId && (
                <PublishingAuditModal
                    contentId={contentId}
                    isOpen={showAuditModal}
                    onClose={() => setShowAuditModal(false)}
                    venueTimezone={venueTimezone}
                />
            )}

            {/* Withdraw Confirmation Modal */}
            <Modal
                isOpen={showWithdrawModal}
                onClose={() => {
                    setShowWithdrawModal(false)
                    setWithdrawReason('')
                }}
                title="Withdraw Match"
                size="medium"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setShowWithdrawModal(false)
                                setWithdrawReason('')
                            }}
                            disabled={isProcessing}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleWithdraw}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Withdrawing...' : 'Confirm Withdraw'}
                        </Button>
                    </>
                }
            >
                <p>
                    Are you sure you want to withdraw this match from publishing?
                    This will send a withdrawal notification to Amagi and the match
                    will be removed from their catalog.
                </p>

                <div className="fieldGroup">
                    <label>Reason for withdrawal (optional)</label>
                    <textarea
                        value={withdrawReason}
                        onChange={(e) => setWithdrawReason(e.target.value)}
                        placeholder="Enter a reason for withdrawing this match..."
                        className="input"
                        rows={3}
                    />
                </div>
            </Modal>

            {/* Cancel Publish Confirmation Modal */}
            <Modal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                title="Cancel Publish"
                size="medium"
                footer={
                    <>
                        <Button
                            variant="secondary"
                            onClick={() => setShowCancelModal(false)}
                            disabled={isProcessing}
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="danger"
                            onClick={() => {
                                setShowCancelModal(false)
                                handleCancel()
                            }}
                            disabled={isProcessing}
                        >
                            {isProcessing ? 'Cancelling...' : 'Confirm Cancel'}
                        </Button>
                    </>
                }
            >
                <p>
                    Are you sure you want to cancel this published match?
                </p>
                <p>
                    This will send a cancellation notification to Amagi indicating
                    the match was cancelled before it started. This action cannot be undone.
                </p>
            </Modal>
        </div>
    )
}


